import React, { FormEvent, useState } from 'react';
import {
  CREATE_GROUP_ELEMENT_NAME,
  GROUP_ELEMENT_TYPE,
  GroupContext,
  MAX_GROUP_NAME_ERROR_MESSAGE,
  MAX_GROUP_NAME_LENGTH,
  PreferenceResponseStatus,
  UNKNOWN_ERROR_MESSAGE,
} from '@utils/userPreferencesUtils/Types';
import { useProductAnalyzerGroupState } from '@utils/hook/userPreferences/UseGroupState';
import { useProductAnalyzerGroupEventState } from '@components/top-selling-products/product-table-grouping/utils/ProductAnalyzerGroupContext';
import {
  groupElementClickedTrackingFunction,
  PRODUCT_LEADERBOARD_TABLE_ELEMENT_CLICKED_TITLE,
  PRODUCT_LEADERBOARD_TABLE_NAME,
  TableElementClickedParam,
} from '@utils/page-analytics/model/GroupElementClickTracking';
import { PageName, PageType } from '@utils/pageConfig/Types';
import { useAnalytics } from '@ecosystem/newron-client-lib';
import { useSelector } from 'react-redux';
import { IRootState } from '@state/reducers/Reducers';
import { useProductsModelCompare } from '@components/products-compare-modal/ProductsModelCompareContext';
import CreateGroupModal from '@components/create-group-modal/CreateGroupModal';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';

export interface Props {
  context: GroupContext;
  sortOrder: number;
}

export const CreateProductLeaderboardGroupModal: React.FC<Props> = ({ context, sortOrder }) => {
  const [groupName, setGroupName] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { createProductAnalyzerGroup } = useProductAnalyzerGroupState(context);
  const {
    selectedModelGroups,
    selectedModelsName,
    clearSelectedModelsGroup,
    isCreateModalGroupPopUp,
    setIsCreateModalGroupPopUp,
  } = useProductAnalyzerGroupEventState();
  const { clearSelectedModels } = useProductsModelCompare();
  const { track } = useAnalytics();
  const pagesConfig = usePageConfig();
  const sideFiltersState = useSelector((state: IRootState) => state.sideFilters);
  const hitlistsRetailerClientState = useSelector((state: any) => state.hitlistsRetailerClient);

  const onCancel = () => {
    setIsSaving(false);
    setError('');
    setGroupName('');
    setIsCreateModalGroupPopUp(false);
  };

  //Function to create group
  const onSaveGroup = (e: FormEvent) => {
    e.preventDefault();

    //Amplitude Tracking when Create Group button is click
    const trackingParam: TableElementClickedParam = {
      eventTitle: PRODUCT_LEADERBOARD_TABLE_ELEMENT_CLICKED_TITLE,
      elementName: CREATE_GROUP_ELEMENT_NAME.model,
      elementType: GROUP_ELEMENT_TYPE.model,
      track: track,
      optionChosen: selectedModelsName.flatMap((selectedModel) => selectedModel).join(', '),
      pageName: PageName.RI_PRODUCT,
      pageType: PageType.RI_PRODUCT,
      sideFilters: sideFiltersState.appliedFilters,
      tableName: PRODUCT_LEADERBOARD_TABLE_NAME,
      access: pagesConfig.access,
    };

    groupElementClickedTrackingFunction(trackingParam);

    const nameTrimmed = (groupName ?? '').trim();

    if (!nameTrimmed || nameTrimmed.length >= MAX_GROUP_NAME_LENGTH) {
      setError(MAX_GROUP_NAME_ERROR_MESSAGE);
      return;
    }

    setIsSaving(true);
    setError('');

    // Call mutate to create group
    createProductAnalyzerGroup.mutate(
      {
        key: nameTrimmed,
        data: {
          modalGroup: selectedModelGroups,
          sortOrder: sortOrder,
          clientId: hitlistsRetailerClientState.clientId,
        },
      },
      {
        onSuccess: (response) => {
          setIsSaving(false);
          if (response.status === PreferenceResponseStatus.SUCCESS) {
            setGroupName('');
            clearSelectedModelsGroup();
            clearSelectedModels();
            onCancel();
          } else {
            setError(response.message);
          }
        },
        onError: () => {
          setError(UNKNOWN_ERROR_MESSAGE);
          setIsSaving(false);
        },
      },
    );
  };

  return (
    <div>
      <CreateGroupModal
        isCreateModalGroupPopUp={isCreateModalGroupPopUp}
        createMessage="Create Model Group"
        onCancel={onCancel}
        isSaving={isSaving}
        onSaveGroup={onSaveGroup}
        title={'Create Product Leaderboard Group'}
        setGroupName={setGroupName}
        error={error}
      />
    </div>
  );
};
